import React from 'react';
import { Droplet, Factory, Truck, Wrench, Shield, Lightbulb, ChevronRight } from 'lucide-react';
import './ServicesPage.css';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const ServicesPage = () => {
  const services = [
    {
      id: 'exploration',
      icon: <Droplet size={40} />,
      title: 'Upstream Oil Exploration',
      shortDesc: 'Advanced exploration and production capabilities',
      image: 'https://images.unsplash.com/photo-1561625101-fd85c8e135f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHxvZmZzaG9yZSUyMG9pbCUyMHJpZ3xlbnwwfHx8fDE3NjUxMzMzNTR8MA&ixlib=rb-4.1.0&q=85',
      details: [
        'Seismic data acquisition and interpretation',
        'Onshore and offshore drilling operations',
        'Well design and completion services',
        'Enhanced oil recovery techniques',
        'Reservoir management and optimization'
      ]
    },
    {
      id: 'refining',
      icon: <Factory size={40} />,
      title: 'Refining & Processing',
      shortDesc: 'State-of-the-art refining facilities and processes',
      image: 'https://images.unsplash.com/photo-1602056820935-316884c035f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxvaWwlMjByZWZpbmVyeXxlbnwwfHx8fDE3NjUxMzMzNDl8MA&ixlib=rb-4.1.0&q=85',
      details: [
        'Crude oil distillation and fractionation',
        'Catalytic cracking and reforming',
        'Hydroprocessing and desulfurization',
        'Petrochemical production',
        'Quality control and product optimization'
      ]
    },
    {
      id: 'distribution',
      icon: <Truck size={40} />,
      title: 'Distribution and Trading',
      shortDesc: 'Comprehensive logistics and supply chain solutions',
      image: 'https://images.pexels.com/photos/257700/pexels-photo-257700.jpeg',
      details: [
        'Domestic and international crude oil trading',
        'Refined products distribution network',
        'Pipeline infrastructure management',
        'Storage terminal operations',
        'Supply chain optimization'
      ]
    },
    {
      id: 'engineering',
      icon: <Wrench size={40} />,
      title: 'Engineering, Maintenance & Consulting',
      shortDesc: 'Expert technical services and consulting',
      image: 'https://images.unsplash.com/photo-1508188609340-e8068b599193?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHw0fHxvaWwlMjB3b3JrZXJzJTIwc2FmZXR5fGVufDB8fHx8MTc2NTEzMzM2MXww&ixlib=rb-4.1.0&q=85',
      details: [
        'Facility design and engineering',
        'Preventive and predictive maintenance',
        'Turnaround planning and execution',
        'Asset integrity management',
        'Technical consulting and feasibility studies'
      ]
    },
    {
      id: 'hse',
      icon: <Shield size={40} />,
      title: 'HSE & Compliance Programs',
      shortDesc: 'Comprehensive health, safety, and environmental management',
      image: 'https://images.pexels.com/photos/28247164/pexels-photo-28247164.jpeg',
      details: [
        'Occupational health and safety programs',
        'Environmental impact assessment',
        'Emergency response planning',
        'Regulatory compliance management',
        'Safety training and certification',
        'Environmental monitoring and reporting'
      ]
    },
    {
      id: 'innovation',
      icon: <Lightbulb size={40} />,
      title: 'Innovation, Technology & Optimization',
      shortDesc: 'Driving efficiency through advanced technology',
      image: 'https://images.unsplash.com/photo-1629540946404-ebe133e99f49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxvZmZzaG9yZSUyMG9pbCUyMHJpZ3xlbnwwfHx8fDE3NjUxMzMzNTR8MA&ixlib=rb-4.1.0&q=85',
      details: [
        'Digital transformation initiatives',
        'Process automation and control systems',
        'Data analytics and AI implementation',
        'Energy efficiency optimization',
        'Research and development programs',
        'Alternative energy integration'
      ]
    }
  ];

  return (
    <div className="services-page">
      <section className="services-hero">
        <div className="services-hero-overlay"></div>
        <div className="services-hero-content">
          <h1>Our Services</h1>
          <p>Comprehensive energy solutions across the entire value chain</p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <h2 className="section-title">What We Offer</h2>
          <p className="section-subtitle">
            From exploration to distribution, we deliver integrated services that power industries and communities worldwide
          </p>

          <div className="services-accordion-wrapper">
            <Accordion type="single" collapsible className="services-accordion">
              {services.map((service, index) => (
                <AccordionItem key={service.id} value={service.id}>
                  <AccordionTrigger className="service-trigger">
                    <div className="service-trigger-content">
                      <div className="service-trigger-icon">{service.icon}</div>
                      <div className="service-trigger-text">
                        <h3>{service.title}</h3>
                        <p>{service.shortDesc}</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="service-content">
                    <div className="service-detail-grid">
                      <div className="service-detail-image">
                        <img src={service.image} alt={service.title} />
                      </div>
                      <div className="service-detail-text">
                        <h4>Key Capabilities</h4>
                        <ul className="service-capabilities">
                          {service.details.map((detail, idx) => (
                            <li key={idx}>
                              <ChevronRight size={18} />
                              <span>{detail}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="service-benefits-section section">
        <div className="container">
          <h2 className="section-title">Why Choose Our Services</h2>
          <div className="benefits-grid">
            <div className="benefit-card">
              <h3>Proven Expertise</h3>
              <p>35+ years of experience delivering reliable energy solutions</p>
            </div>
            <div className="benefit-card">
              <h3>Advanced Technology</h3>
              <p>State-of-the-art facilities and cutting-edge processes</p>
            </div>
            <div className="benefit-card">
              <h3>Safety Excellence</h3>
              <p>Industry-leading safety standards and compliance</p>
            </div>
            <div className="benefit-card">
              <h3>Sustainable Practices</h3>
              <p>Committed to environmental responsibility and innovation</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;